import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  Button,
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import useFetchMeals from "../hooks/useFetchMeals";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import useIsMobile from "../hooks/useIsMobile";
import { isVirtualServiceBundle } from "../utils/bundleUtils";

function PopUp({ open, handleClose, bundle, addToCart }) {
  const isMobile = useIsMobile();
  const tabsRef = useRef(null);
  const { meals, loading, error, refetch } = useFetchMeals();
  const total = bundle?.count ?? 0;
  const [mealQty, setMealQty] = useState({});
  const { t } = useTranslation();

  const [errors, setErrors] = useState({});
  const [currentWeek, setCurrentWeek] = useState(0);

  const bundleIsPredefined = bundle
    ? !!(bundle.meals && bundle.meals.length)
    : false;

  const calculateWeeks = (mealCount) => {
    return Math.ceil(mealCount / 7);
  };

  const weeks = calculateWeeks(total);
  const mealsPerWeek = Math.min(7, Math.ceil(total / weeks));

  const validateSelectedBundleCount = () => {
    const count = Object.values(mealQty).reduce(
      (sum, qty) => sum + qty.reduce((a, b) => a + b, 0),
      0
    );
    return count === total;
  };

  useEffect(() => {
    if (open && meals.length && bundle) {
      const bundleMealsById = (bundle.meals || []).reduce((acc, meal) => {
        acc[meal.id] = meal.qty;
        return acc;
      }, {});

      const initial = {};
      meals.forEach((m) => {
        if (bundleIsPredefined) {
          // For predefined bundles, set the quantities as specified
          initial[m.id] = Array(weeks).fill(bundleMealsById[m.id] || 0);
        } else {
          // For custom bundles, initialize with zeros
          initial[m.id] = Array(weeks).fill(0);
        }
      });
      setMealQty(initial);
    }
  }, [meals, bundle, open, weeks, bundleIsPredefined]);

  useEffect(() => {
    if (!open) {
      setMealQty({});
      setErrors({});
      setCurrentWeek(0);
    }
  }, [open]);

  useEffect(() => {
    if (errors.message) {
      const count = Object.values(mealQty).reduce(
        (sum, qty) => sum + qty.reduce((a, b) => a + b, 0),
        0
      );
      if (total === count) {
        setErrors((prev) => ({ ...prev, message: "" }));
      }
    }
  }, [mealQty, total, errors.message]);

  useEffect(() => {
    const container = tabsRef.current;
    if (container) {
      container.scrollLeft = 0; // Reset scroll position when component mounts or updates
    }
  }, [weeks]);
  if (!bundle) return null;
  if (isVirtualServiceBundle(bundle)) return null;

  const handleQtyChange = (mealId) => (event) => {
    if (bundleIsPredefined) return; // Prevent changes for predefined bundles
    const value = parseInt(event.target.value, 10);
    setMealQty((q) => ({
      ...q,
      [mealId]: q[mealId].map((qty, index) =>
        index === currentWeek ? value : qty
      ),
    }));
  };

  const scrollTabs = (direction) => {
    const container = tabsRef.current;
    if (container) {
      const scrollAmount = 100; // Adjust this value as needed
      container.scrollBy({
        left: direction === "right" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };
  const handleAddToCart = () => {
    if (!validateSelectedBundleCount()) {
      setErrors({ ...errors, message: t("error.bundle_not_full") });
      return;
    }

    // Organize meals by week
    const mealsByWeek = Array(weeks)
      .fill()
      .map(() => []);

    meals.forEach((meal) => {
      mealQty[meal.id].forEach((qty, weekIndex) => {
        if (qty > 0) {
          mealsByWeek[weekIndex].push({
            id: meal.id,
            name: meal.name,
            qty: qty,
          });
        }
      });
    });

    const uniqueId = `${bundle.id}-${uuidv4().slice(0, 6)}`;

    addToCart({
      id: uniqueId,
      baseBundleId: bundle.id,
      name: bundle.name,
      price: bundle.price,
      qty: 1,
      meals: mealsByWeek.flat(), // Flatten the array for compatibility with existing structure
      mealsByWeek: mealsByWeek, // Add this new property for week-based organization
      description: bundle.description,
      imageUrl: bundle.imageUrl,
      totalMeals: total,
      weeks: weeks,
      excludeDeliveryFee: !!(
        bundle.excludeDeliveryFee ?? bundle.exclude_delivery_fee
      ),
    });

    handleClose();
  };

  const totalSelected = Object.values(mealQty).reduce(
    (sum, weekQty) => sum + weekQty.reduce((weekSum, qty) => weekSum + qty, 0),
    0
  );
  const remaining = total - totalSelected;

  return (
    <div
      onTouchStart={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: "0 16px 48px rgba(15, 77, 40, 0.18)",
          },
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          sx={{
            bgcolor: "#f3faf5",
            borderBottom: "1px solid rgba(27,122,61,0.1)",
            alignItems: "center",
          }}
        >
          <IconButton onClick={handleClose} sx={{ pr: 3 }}>
            <HighlightOffIcon />
          </IconButton>
          <DialogTitle
            sx={{ color: "#0f4d28", fontWeight: 700, fontFamily: "Tajawal" }}
          >
            {bundle.name}
          </DialogTitle>
        </Box>
        <Typography gutterBottom align="right" pl={3} pb={2}>
          {bundle.description}
        </Typography>
        <DialogContent>
          {!bundleIsPredefined && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                paddingBottom: 2,
              }}
            >
              <IconButton onClick={() => scrollTabs("right")}>
                <ArrowBackIosNewIcon />
              </IconButton>
              <Box
                ref={tabsRef}
                sx={{
                  display: "flex",
                  overflowX: "auto",
                  scrollbarWidth: "none",
                  "&::-webkit-scrollbar": { display: "none" },
                  "& > *": { flex: "0 0 auto" },
                }}
              >
                {Array.from({ length: weeks }, (_, i) => (
                  <Button
                    key={i}
                    onClick={() => setCurrentWeek(i)}
                    variant={currentWeek === i ? "contained" : "outlined"}
                    color="success"
                    sx={{ mx: 0.5 }}
                  >
                    {`${t("Week")} ${i + 1}`}
                  </Button>
                ))}
              </Box>
              <IconButton onClick={() => scrollTabs("left")}>
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>
          )}
          <Typography gutterBottom align="right" pl={3} pb={2}>
            {remaining > 0
              ? t("select_remaining", { count: remaining })
              : t("all_meals_selected")}
          </Typography>

          {meals.map((meal) => {
            const currentValue = mealQty[meal.id]?.[currentWeek] ?? 0;
            const maxForThis = remaining + currentValue;
            let options = Array.from({ length: maxForThis + 1 }, (_, i) => i);

            return (
              <Box
                key={meal.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography>{meal.name}</Typography>
                {bundleIsPredefined ? (
                  <Typography>
                    {mealQty[meal.id]?.[currentWeek] ?? 0}
                  </Typography>
                ) : (
                  <FormControl
                    variant="standard"
                    size="small"
                    sx={{
                      minWidth: 64,
                      "& .MuiInput-underline:before": { borderBottom: 0 },
                    }}
                  >
                    <Select
                      value={mealQty[meal.id]?.[currentWeek] ?? 0}
                      onChange={handleQtyChange(meal.id)}
                      displayEmpty
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 300,
                          },
                        },
                      }}
                    >
                      {options.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            );
          })}
        </DialogContent>
        {errors.message && (
          <Typography
            color="error"
            align="center"
            sx={{
              mb: 2,
              fontFamily: "Tajawal",
            }}
          >
            {errors.message}
          </Typography>
        )}
        <Button
          sx={{
            width: "100%",
            minHeight: "50px",
            backgroundColor: "#eaf7ef",
            color: "#0f4d28",
            justifyContent: "center",
            fontFamily: "Tajawal",
          }}
          onClick={handleAddToCart}
        >
          {t("addToCart")}
        </Button>
      </Dialog>
    </div>
  );
}

export default PopUp;
